import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Play, Pause, RotateCcw, RotateCw, Volume2, Volume1, VolumeX,
  Maximize, Minimize, PictureInPicture2, Gauge, Download, Loader2, TriangleAlert,
} from 'lucide-react';
import { formatClock } from '../lib/media.js';

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const CONTROLS_HIDE_MS = 2600;

/** Stažený soubor má nést název hodiny, ale příponu si vezme z adresy. */
function downloadAs(name, src) {
  if (!name) return undefined;
  const ext = /\.([a-z0-9]{2,4})(?:[?#]|$)/i.exec(src);
  return ext ? `${name}.${ext[1].toLowerCase()}` : name;
}

/** Starší Safari zná jen prefixovanou podobu fullscreen API. */
const fullscreenNode = () => document.fullscreenElement ?? document.webkitFullscreenElement ?? null;

/** Kde student minule skončil. Klíčem je adresa záznamu. */
const resumeKey = (src) => `seminar:zaznam:${src}`;

function readResume(src) {
  try {
    return Number(window.localStorage.getItem(resumeKey(src))) || 0;
  } catch {
    return 0;
  }
}

function writeResume(src, seconds) {
  try {
    if (seconds > 20) window.localStorage.setItem(resumeKey(src), String(Math.floor(seconds)));
    else window.localStorage.removeItem(resumeKey(src));
  } catch {
    /* soukromé okno, nevadí */
  }
}

/**
 * Přehrávač záznamů z hodin.
 *
 * Vlastní ovládání místo `controls` je tu kvůli věcem, které nativní
 * přehrávač neumí napříč prohlížeči: rychlost přehrávání jedním klepnutím,
 * skoky po deseti vteřinách, klávesové zkratky a hlavně návrat tam, kde
 * student minule přestal — u hodinového záznamu to je to nejdůležitější.
 */
export default function MediaPlayer({ src, poster, title, kind = 'video', downloadName }) {
  const shellRef = useRef(null);
  const mediaRef = useRef(null);
  const barRef = useRef(null);
  const hideTimer = useRef(null);
  const wasPlaying = useRef(false);
  const scrubbingRef = useRef(false);
  const resumeApplied = useRef(null);

  const audioOnly = kind === 'audio';

  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [rate, setRate] = useState(1);
  const [speedOpen, setSpeedOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [pip, setPip] = useState(false);
  const [scrubbing, setScrubbing] = useState(false);
  const [hover, setHover] = useState(null);      // náhled času pod kurzorem
  const [resumedFrom, setResumedFrom] = useState(0);
  const [controlsShown, setControlsShown] = useState(true);

  // ------------------------------------------------------------- ovládání

  const media = () => mediaRef.current;

  const showControls = useCallback(() => {
    setControlsShown(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      // ovládání mizí jen když se opravdu hraje – u pauzy by to jen mátlo
      if (mediaRef.current && !mediaRef.current.paused) setControlsShown(false);
    }, CONTROLS_HIDE_MS);
  }, []);

  const togglePlay = useCallback(() => {
    const node = media();
    if (!node) return;
    if (node.paused) node.play().catch(() => setFailed(true));
    else node.pause();
    showControls();
  }, [showControls]);

  const seekTo = useCallback((seconds) => {
    const node = media();
    if (!node || !Number.isFinite(seconds)) return;
    const max = node.duration || 0;
    node.currentTime = Math.max(0, Math.min(seconds, max ? max - 0.05 : seconds));
    setTime(node.currentTime);
    showControls();
  }, [showControls]);

  const skip = useCallback((delta) => seekTo((media()?.currentTime || 0) + delta), [seekTo]);

  const changeVolume = useCallback((value) => {
    const node = media();
    if (!node) return;
    node.volume = Math.max(0, Math.min(1, value));
    node.muted = node.volume === 0;
    showControls();
  }, [showControls]);

  const toggleMute = useCallback(() => {
    const node = media();
    if (!node) return;
    node.muted = !node.muted;
    if (!node.muted && node.volume === 0) node.volume = 0.5;
    showControls();
  }, [showControls]);

  const setSpeed = useCallback((value) => {
    const node = media();
    if (node) node.playbackRate = value;
    setSpeedOpen(false);
    showControls();
  }, [showControls]);

  const toggleFullscreen = useCallback(() => {
    const shell = shellRef.current;
    const node = media();
    if (fullscreenNode()) {
      (document.exitFullscreen ?? document.webkitExitFullscreen)?.call(document);
    } else if (shell?.requestFullscreen) {
      shell.requestFullscreen().catch(() => {});
    } else if (shell?.webkitRequestFullscreen) {
      shell.webkitRequestFullscreen(); // starší Safari
    } else if (node?.webkitEnterFullscreen) {
      node.webkitEnterFullscreen(); // iPhone umí celou obrazovku jen na videu
    }
  }, []);

  const togglePip = useCallback(async () => {
    const node = media();
    if (!node || audioOnly) return;
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
      else await node.requestPictureInPicture();
    } catch {
      /* prohlížeč to odmítl, nic se neděje */
    }
  }, [audioOnly]);

  // -------------------------------------------------- napojení na <video>

  useEffect(() => {
    const node = media();
    if (!node) return undefined;

    const onLoaded = () => {
      setReady(true);
      setDuration(node.duration || 0);

      // navázat na minule smíme jen jednou, jinak by každé překreslení
      // odskočilo zpátky doprostřed záznamu
      if (resumeApplied.current === src) return;
      resumeApplied.current = src;

      const saved = readResume(src);
      if (saved > 20 && node.duration && saved < node.duration - 20) {
        node.currentTime = saved;
        setResumedFrom(saved);
      }
    };
    const onTime = () => {
      if (!scrubbingRef.current) setTime(node.currentTime);
      writeResume(src, node.currentTime);
    };
    const onProgress = () => {
      const ranges = node.buffered;
      for (let i = ranges.length - 1; i >= 0; i -= 1) {
        if (ranges.start(i) <= node.currentTime) return setBuffered(ranges.end(i));
      }
      setBuffered(ranges.length ? ranges.end(ranges.length - 1) : 0);
    };
    const onVolume = () => { setVolume(node.volume); setMuted(node.muted); };
    const onEnded = () => { setPlaying(false); writeResume(src, 0); setControlsShown(true); };

    const events = {
      loadedmetadata: onLoaded,
      durationchange: () => setDuration(node.duration || 0),
      timeupdate: onTime,
      progress: onProgress,
      volumechange: onVolume,
      ratechange: () => setRate(node.playbackRate),
      play: () => { setPlaying(true); showControls(); },
      pause: () => { setPlaying(false); setControlsShown(true); },
      waiting: () => setWaiting(true),
      playing: () => { setWaiting(false); setPlaying(true); },
      canplay: () => setWaiting(false),
      ended: onEnded,
      error: () => setFailed(true),
      enterpictureinpicture: () => setPip(true),
      leavepictureinpicture: () => setPip(false),
    };
    Object.entries(events).forEach(([name, handler]) => node.addEventListener(name, handler));

    if (node.readyState >= 1) onLoaded();
    onVolume();

    return () => Object.entries(events).forEach(([name, handler]) => node.removeEventListener(name, handler));
  }, [src, showControls]);

  useEffect(() => {
    const onChange = () => setFullscreen(fullscreenNode() === shellRef.current);
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  // nabídku rychlostí zavře klepnutí vedle ní
  useEffect(() => {
    if (!speedOpen) return undefined;
    const close = (event) => {
      if (!event.target.closest?.('.player-speed')) setSpeedOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [speedOpen]);

  // ------------------------------------------------------------- posuvník

  const positionFromEvent = (event) => {
    const bar = barRef.current;
    if (!bar || !duration) return 0;
    const rect = bar.getBoundingClientRect();
    const x = (event.touches?.[0]?.clientX ?? event.clientX) - rect.left;
    return Math.max(0, Math.min(1, x / rect.width)) * duration;
  };

  useEffect(() => {
    if (!scrubbing) return undefined;

    const onMove = (event) => {
      event.preventDefault();
      const seconds = positionFromEvent(event);
      setTime(seconds);
      setHover(seconds);
    };
    const onUp = (event) => {
      const node = media();
      if (node) node.currentTime = positionFromEvent(event);
      scrubbingRef.current = false;
      setScrubbing(false);
      setHover(null);
      if (wasPlaying.current) node?.play().catch(() => {});
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [scrubbing, duration]);

  const startScrub = (event) => {
    const node = media();
    if (!node || !duration) return;
    wasPlaying.current = !node.paused;
    node.pause();
    scrubbingRef.current = true;
    setScrubbing(true);
    setTime(positionFromEvent(event));
  };

  // ----------------------------------------------------------- klávesnice

  const onKeyDown = (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const key = event.key;
    const handlers = {
      ' ': togglePlay, k: togglePlay, K: togglePlay,
      ArrowLeft: () => skip(-5), ArrowRight: () => skip(5),
      j: () => skip(-10), J: () => skip(-10), l: () => skip(10), L: () => skip(10),
      ArrowUp: () => changeVolume((media()?.volume ?? 1) + 0.05),
      ArrowDown: () => changeVolume((media()?.volume ?? 1) - 0.05),
      m: toggleMute, M: toggleMute,
      f: toggleFullscreen, F: toggleFullscreen,
      p: togglePip, P: togglePip,
      Home: () => seekTo(0),
      End: () => seekTo(duration),
      ',': () => setSpeed(SPEEDS[Math.max(0, SPEEDS.indexOf(rate) - 1)] ?? 1),
      '.': () => setSpeed(SPEEDS[Math.min(SPEEDS.length - 1, SPEEDS.indexOf(rate) + 1)] ?? 1),
    };

    if (handlers[key]) {
      event.preventDefault();
      handlers[key]();
      showControls();
      return;
    }
    if (/^[0-9]$/.test(key) && duration) {
      event.preventDefault();
      seekTo((Number(key) / 10) * duration);
    }
  };

  // -------------------------------------------------------------- vykresl.

  const shown = time || 0;
  const percent = duration ? (shown / duration) * 100 : 0;
  const bufferedPercent = duration ? Math.min(100, (buffered / duration) * 100) : 0;
  const VolumeIcon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;
  const MediaTag = audioOnly ? 'audio' : 'video';

  if (failed) {
    return (
      <div className="player player-error">
        <TriangleAlert size={20} aria-hidden="true" />
        <div>
          <strong>Záznam se nepodařilo přehrát.</strong>
          <p>Zkuste stránku načíst znovu, nebo si soubor stáhněte a pusťte v přehrávači.</p>
        </div>
        <a className="btn btn-sm" href={src} download={downloadAs(downloadName, src)}>
          <Download size={14} aria-hidden="true" /> Stáhnout
        </a>
      </div>
    );
  }

  return (
    <div
      ref={shellRef}
      className="player"
      data-audio={audioOnly ? '' : undefined}
      data-idle={!controlsShown && playing ? '' : undefined}
      tabIndex={0}
      role="region"
      aria-label={title ? `Přehrávač: ${title}` : 'Přehrávač záznamu'}
      onKeyDown={onKeyDown}
      onMouseMove={showControls}
      onMouseLeave={() => playing && setControlsShown(false)}
      onTouchStart={showControls}
    >
      <MediaTag
        ref={mediaRef}
        className="player-media"
        src={src}
        preload="metadata"
        {...(audioOnly ? {} : {
          poster: poster || undefined,
          playsInline: true,
          onClick: togglePlay,
          onDoubleClick: toggleFullscreen,
        })}
      />

      {!audioOnly && (
        <>
          {waiting && (
            <div className="player-spinner" role="status" aria-label="Načítá se">
              <Loader2 size={34} aria-hidden="true" />
            </div>
          )}

          {!playing && !waiting && (
            <button type="button" className="player-big" onClick={togglePlay} aria-label="Přehrát">
              <Play size={28} aria-hidden="true" />
            </button>
          )}
        </>
      )}

      {resumedFrom > 0 && (
        <div className="player-resume">
          <span>Pokračujeme od {formatClock(resumedFrom)}</span>
          <button
            type="button"
            onClick={() => { seekTo(0); setResumedFrom(0); }}
          >
            Od začátku
          </button>
          <button type="button" aria-label="Skrýt" onClick={() => setResumedFrom(0)}>✕</button>
        </div>
      )}

      <div className="player-controls">
        <div
          ref={barRef}
          className="player-bar"
          role="slider"
          tabIndex={-1}
          aria-label="Pozice v záznamu"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(shown)}
          aria-valuetext={`${formatClock(shown)} z ${formatClock(duration)}`}
          onMouseDown={startScrub}
          onTouchStart={startScrub}
          onMouseMove={(event) => !scrubbing && setHover(positionFromEvent(event))}
          onMouseLeave={() => !scrubbing && setHover(null)}
        >
          <div className="player-bar-track">
            <div className="player-bar-buffer" style={{ width: `${bufferedPercent}%` }} />
            <div className="player-bar-fill" style={{ width: `${percent}%` }} />
          </div>
          <div className="player-bar-knob" style={{ left: `${percent}%` }} />
          {hover !== null && duration > 0 && (
            <div className="player-bar-tip" style={{ left: `${(hover / duration) * 100}%` }}>
              {formatClock(hover)}
            </div>
          )}
        </div>

        <div className="player-buttons">
          <button type="button" onClick={togglePlay} aria-label={playing ? 'Pozastavit' : 'Přehrát'}>
            {playing ? <Pause size={18} aria-hidden="true" /> : <Play size={18} aria-hidden="true" />}
          </button>
          <button type="button" onClick={() => skip(-10)} aria-label="Zpět o 10 vteřin">
            <RotateCcw size={17} aria-hidden="true" />
          </button>
          <button type="button" onClick={() => skip(10)} aria-label="Vpřed o 10 vteřin">
            <RotateCw size={17} aria-hidden="true" />
          </button>

          <span className="player-time">
            {formatClock(shown)}<span> / {ready ? formatClock(duration) : '—'}</span>
          </span>

          <div className="player-volume">
            <button type="button" onClick={toggleMute} aria-label={muted ? 'Zapnout zvuk' : 'Ztlumit'}>
              <VolumeIcon size={18} aria-hidden="true" />
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={muted ? 0 : volume}
              onChange={(event) => changeVolume(Number(event.target.value))}
              aria-label="Hlasitost"
            />
          </div>

          <div className="player-speed">
            <button
              type="button"
              onClick={() => setSpeedOpen((open) => !open)}
              aria-label="Rychlost přehrávání"
              aria-expanded={speedOpen}
              data-active={rate !== 1 ? '' : undefined}
            >
              <Gauge size={18} aria-hidden="true" />
              {rate !== 1 && <small>{String(rate).replace('.', ',')}×</small>}
            </button>
            {speedOpen && (
              <div className="player-menu" role="menu">
                {SPEEDS.map((speed) => (
                  <button
                    key={speed}
                    type="button"
                    role="menuitemradio"
                    aria-checked={speed === rate}
                    onClick={() => setSpeed(speed)}
                  >
                    {speed === 1 ? 'Normální' : `${String(speed).replace('.', ',')}×`}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a className="player-download" href={src} download={downloadAs(downloadName, src)} aria-label="Stáhnout záznam">
            <Download size={17} aria-hidden="true" />
          </a>

          {!audioOnly && document.pictureInPictureEnabled && (
            <button type="button" className="player-pip" onClick={togglePip} aria-label="Obraz v obraze" data-active={pip ? '' : undefined}>
              <PictureInPicture2 size={17} aria-hidden="true" />
            </button>
          )}

          {!audioOnly && (
            <button type="button" onClick={toggleFullscreen} aria-label={fullscreen ? 'Zpět z celé obrazovky' : 'Celá obrazovka'}>
              {fullscreen ? <Minimize size={17} aria-hidden="true" /> : <Maximize size={17} aria-hidden="true" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
